import React from "react"
import { useDispatch } from "react-redux"
import { useGeolocated } from "react-geolocated"
import MyLocationIcon from "@mui/icons-material/MyLocation"

import { Chip } from "@mui/material"
import { reverseGeocode } from "../../utils/locationUtils"
import { logError } from "../../utils/errors"
import { setSnackbar } from "../../actions/globalUiActions"
import { track } from "../../utils/analytics"
import Loading from "./Loading"

interface Props {
  callback: (address: string) => void
}

const Geolocator = ({ callback }: Props) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isHidden, setIsHidden] = React.useState(false)
  const { getPosition } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
    },
    suppressLocationOnMount: true,
    onSuccess: async ({ coords }) => {
      await reverseGeocode(coords.latitude, coords.longitude)
        .then((response) => {
          callback(response.formattedAddress)
          setIsLoading(false)
          setIsHidden(true)
        })
        .catch((error) => {
          setIsLoading(false)
          logError(error)
          dispatch(setSnackbar("error", "Error fetching location data."))
        })
    },
    onError: () => {
      // This would likely be a device/permissions error, so should be safe to not log.
      setIsLoading(false)
      dispatch(
        setSnackbar(
          "We were unable to access your location. Please enter your address manually.",
          "error",
        ),
      )
    },
  })

  if (isLoading) return <Loading />

  return (
    <Chip
      className={isHidden ? "hidden" : ""}
      icon={<MyLocationIcon />}
      variant="filled"
      label="Use My Location"
      color="primary"
      onClick={() => {
        setIsLoading(true)
        track("Use Current Location Clicked")
        getPosition()
      }}
    />
  )
}

export default Geolocator
